/**
 * components/wizard/FrameZoom.jsx — 🆕 2026-08-23 · Zoom, Verschieben und
 * Lupe im Zeichenbereich.
 *
 * Warum
 * -----
 * Der Anker-Frame kommt als 1280×720-Proxy und wurde mit `w-full h-auto`
 * dargestellt — es gab **keinen** Zoom. Wer eine Feldlinie pixelgenau
 * nachziehen wollte, hat den ganzen BROWSER vergrößert, gezeichnet und
 * wieder verkleinert. Das ist der Vorgang, den dieses Modul ersetzt.
 *
 * Der Trick, der alles einfach hält
 * ---------------------------------
 * Zoom und Verschieben passieren als CSS-`transform` auf einem Wrapper
 * INNERHALB des Rahmens, und zwar um das Bild **und** die Overlays
 * gemeinsam. Damit liefert `img.getBoundingClientRect()` von sich aus die
 * transformierte Lage, und die vorhandene Umrechnung
 *
 *     x = ((e.clientX - rect.left) / rect.width) * imgDims.w
 *
 * stimmt weiter — ohne eine einzige Änderung an der Klick-Mathematik.
 * Genau deshalb liegt der Transform nicht auf dem äußeren Rahmen: der
 * müsste sonst aus jeder Koordinate herausgerechnet werden.
 *
 * `transform-origin: 0 0` ist Voraussetzung für die Zoom-auf-Punkt-Formel
 * unten; mit zentriertem Ursprung wäre sie falsch.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 8;
export const ZOOM_STEP = 1.2;
// Die Lupe zeigt IMMER `LOUPE_MAG`-fach mehr Detail als das, was gerade zu
// sehen ist — nicht einen festen Faktor auf das Originalbild. Sonst wäre
// sie bei 4-fachem Zoom plötzlich gröber als die Ansicht.
export const LOUPE_MAG = 2.5;
export const LOUPE_SIZE = 168;

/**
 * Zoom-/Verschiebe-Zustand für einen Bild-Rahmen.
 *
 * `viewportRef` muss auf das Element mit `overflow: hidden` zeigen; der
 * Inhalt darin bekommt `contentStyle`.
 */
export function useFrameZoom(viewportRef, { enabled = true } = {}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);
  const panRef = useRef(null);
  // Für die Wheel-Behandlung: der Effekt darf nicht bei jeder
  // Zoom-Änderung neu binden, sonst geht ein schnelles Scrollen verloren.
  const stateRef = useRef({ zoom: 1, pan: { x: 0, y: 0 } });
  stateRef.current = { zoom, pan };

  const clampPan = useCallback((p, z) => {
    const vp = viewportRef.current;
    if (!vp) return p;
    const w = vp.clientWidth;
    const h = vp.clientHeight;
    // Bei transform-origin 0 0 und Inhaltsgröße = Viewportgröße × z darf
    // pan zwischen (1 − z) × Größe und 0 liegen — dann bleibt der
    // Rahmen immer voll gefüllt, das Bild kann nicht herausrutschen.
    return {
      x: Math.min(0, Math.max(w * (1 - z), p.x)),
      y: Math.min(0, Math.max(h * (1 - z), p.y)),
    };
  }, [viewportRef]);

  /** Zoomt um einen Bildschirmpunkt herum (Mausrad, Doppelklick). */
  const zoomAt = useCallback((faktor, clientX, clientY) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    const { zoom: z0, pan: p0 } = stateRef.current;
    const z1 = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z0 * faktor));
    if (z1 === z0) return;
    // Punkt unter dem Cursor festhalten:
    //   c = (m − pan) / z   (Inhaltskoordinate)
    //   pan' = m − z' · c
    const mx = (clientX ?? r.left + r.width / 2) - r.left;
    const my = (clientY ?? r.top + r.height / 2) - r.top;
    const next = {
      x: mx - (z1 / z0) * (mx - p0.x),
      y: my - (z1 / z0) * (my - p0.y),
    };
    setZoom(z1);
    setPan(clampPan(z1 === 1 ? { x: 0, y: 0 } : next, z1));
  }, [viewportRef, clampPan]);

  const reset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Mausrad — bewusst OHNE Modifikator: der Rahmen ist eine
  // Arbeitsfläche, kein Seiteninhalt. `passive: false` ist Pflicht,
  // sonst greift preventDefault nicht und die Seite scrollt mit.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !enabled) return undefined;
    const onWheel = (e) => {
      const rein = e.deltaY < 0;
      const z = stateRef.current.zoom;
      // Wenn die Geste den Zoom gar nicht ändern kann (bei 1× noch weiter
      // raus, bei 8× noch weiter rein), NICHT abfangen — dann scrollt die
      // Seite normal weiter. Sonst schluckt der Rahmen das Mausrad, ohne
      // etwas zu tun, und man kommt über dem Bild nicht mehr vom Fleck.
      if ((!rein && z <= ZOOM_MIN) || (rein && z >= ZOOM_MAX)) return;
      e.preventDefault();
      zoomAt(rein ? ZOOM_STEP : 1 / ZOOM_STEP, e.clientX, e.clientY);
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, [viewportRef, enabled, zoomAt]);

  // Leertaste = Schiebe-Modus. Gehalten, nicht umgeschaltet — so kann man
  // nicht versehentlich darin hängen bleiben.
  useEffect(() => {
    if (!enabled) return undefined;
    const inFeld = (e) => ["input", "textarea", "select"].includes(
      (e.target?.tagName || "").toLowerCase());
    const down = (e) => {
      if (e.code === "Space" && !inFeld(e)) { e.preventDefault(); setSpaceHeld(true); }
    };
    const up = (e) => { if (e.code === "Space") setSpaceHeld(false); };
    const blur = () => setSpaceHeld(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, [enabled]);

  /** True, wenn dieses mousedown das Bild schieben soll statt zu zeichnen. */
  const isPanEvent = useCallback(
    (e) => enabled && (e.button === 1 || (e.button === 0 && spaceHeld)),
    [enabled, spaceHeld]);

  const startPan = useCallback((e) => {
    e.preventDefault();
    panRef.current = { x: e.clientX, y: e.clientY, pan: stateRef.current.pan };
    setPanning(true);
  }, []);

  useEffect(() => {
    if (!panning) return undefined;
    const move = (e) => {
      const s = panRef.current;
      if (!s) return;
      setPan(clampPan({
        x: s.pan.x + (e.clientX - s.x),
        y: s.pan.y + (e.clientY - s.y),
      }, stateRef.current.zoom));
    };
    const up = () => { panRef.current = null; setPanning(false); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [panning, clampPan]);

  // Bei Größenänderung des Fensters kann pan ungültig werden.
  useEffect(() => {
    const onResize = () => setPan((p) => clampPan(p, stateRef.current.zoom));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPan]);

  return {
    zoom, pan, panning, spaceHeld,
    zoomAt, reset, isPanEvent, startPan,
    zoomIn: () => zoomAt(ZOOM_STEP),
    zoomOut: () => zoomAt(1 / ZOOM_STEP),
    contentStyle: {
      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
      transformOrigin: "0 0",
      // Beim Zoomen die Pixel NICHT weichzeichnen — beim Nachziehen einer
      // Feldlinie will man die harte Kante sehen, nicht ihre Interpolation.
      imageRendering: zoom > 1.5 ? "pixelated" : "auto",
    },
    cursor: panning ? "grabbing" : spaceHeld ? "grab" : "crosshair",
  };
}

/**
 * Lupe am Cursor. Liegt AUSSERHALB des Transforms, wird also selbst nicht
 * mitskaliert, und ist `pointer-events: none` — sie darf dem Zeichnen
 * nicht in die Quere kommen.
 *
 * `natural` ist der Cursor in Bildpixeln, `view` seine Lage im Rahmen.
 * Die Lupe weicht dem Cursor aus (springt auf die andere Seite, wenn sie
 * sonst aus dem Rahmen läuft) — sonst verdeckt sie genau die Stelle, die
 * man treffen will.
 */
export const FrameLoupe = ({
  src, imgDims, natural, view, viewportSize, zoom = 1,
  mag = LOUPE_MAG, size = LOUPE_SIZE,
}) => {
  if (!natural || !view || !imgDims?.w || !viewportSize?.w) return null;
  // Bildschirm-Maßstab der aktuellen Ansicht, dann `mag`-fach feiner.
  const s = (viewportSize.w * zoom / imgDims.w) * mag;
  const rand = 12;
  const rechts = view.x + rand + size > viewportSize.w;
  const unten = view.y + rand + size > viewportSize.h;
  const left = rechts ? view.x - rand - size : view.x + rand;
  const top = unten ? view.y - rand - size : view.y + rand;
  return (
    <div
      data-testid="frame-loupe"
      className="absolute pointer-events-none rounded-full border-2 border-cyan-400/80 shadow-xl overflow-hidden"
      style={{
        left: Math.max(0, Math.min(viewportSize.w - size, left)),
        top: Math.max(0, Math.min(viewportSize.h - size, top)),
        width: size, height: size,
        backgroundImage: `url(${src})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: `${imgDims.w * s}px ${imgDims.h * s}px`,
        backgroundPosition:
          `${-(natural.x * s - size / 2)}px ${-(natural.y * s - size / 2)}px`,
        imageRendering: "pixelated",
      }}
    >
      {/* Fadenkreuz auf der exakten Cursor-Position */}
      <div className="absolute inset-x-0 top-1/2 h-px bg-cyan-300/70" />
      <div className="absolute inset-y-0 left-1/2 w-px bg-cyan-300/70" />
      <div className="absolute left-1/2 top-1/2 w-2 h-2 -ml-1 -mt-1 rounded-full border border-cyan-200/90" />
      <div className="absolute bottom-0 inset-x-0 text-center font-mono text-[9px] bg-black/55 text-cyan-200">
        {mag}× · {Math.round(natural.x)},{Math.round(natural.y)}
      </div>
    </div>
  );
};

/** Kleine Bedienleiste in der Rahmenecke — außerhalb des Transforms. */
export const FrameZoomControls = ({
  zoom, onIn, onOut, onReset, loupe, onLoupeToggle,
}) => (
  <div
    data-testid="frame-zoom-controls"
    className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-sm border border-border bg-black/65 px-1 py-0.5 font-mono text-[10px] backdrop-blur-sm"
  >
    <button
      type="button" data-testid="frame-zoom-out" onClick={onOut}
      disabled={zoom <= ZOOM_MIN}
      className="px-1.5 text-foreground/80 hover:text-cyan-300 disabled:opacity-30"
      title="Auszoomen (Mausrad nach unten)"
    >−</button>
    <button
      type="button" data-testid="frame-zoom-reset" onClick={onReset}
      className="px-1 tabular-nums text-cyan-300 hover:text-cyan-200"
      title="Zoom zurücksetzen (Taste 0)"
    >{zoom.toFixed(1)}×</button>
    <button
      type="button" data-testid="frame-zoom-in" onClick={onIn}
      disabled={zoom >= ZOOM_MAX}
      className="px-1.5 text-foreground/80 hover:text-cyan-300 disabled:opacity-30"
      title="Einzoomen (Mausrad nach oben) · Leertaste halten und ziehen verschiebt"
    >+</button>
    <span className="w-px self-stretch bg-border" />
    <button
      type="button" data-testid="frame-loupe-toggle" onClick={onLoupeToggle}
      className={`px-1.5 ${loupe ? "text-cyan-300" : "text-foreground/60 hover:text-cyan-300"}`}
      title={`Lupe ${loupe ? "aus" : "an"} (Taste L) — zeigt ${LOUPE_MAG}× mehr Detail als die Ansicht, ohne den Überblick zu verlieren`}
    >🔍</button>
  </div>
);
